import React, { useEffect, useState, useCallback, MouseEvent, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "@/lib/hooks";
import {
  properties,
  getProperties,
  getPropertiesLoading,
  getUserCurrency,
  getUserCurrentCountry,
} from "@/lib/features/home/homeSlice";
import {
  loading,
  getHotels,
  getSearchPayload,
  saveSearchPayload,
} from "@/lib/features/hotel/hotelSlice";

import { ArrowClockwise, GeoAlt, Buildings, XLg } from "react-bootstrap-icons";
import { Dropdown, DropdownToggle, DropdownMenu } from "reactstrap";
import Skeleton from "react-loading-skeleton";
import { ErrorMessage, useField, useFormikContext } from "formik"; // Import formik hook
import "react-loading-skeleton/dist/skeleton.css"; // Import skeleton CSS
import styles from "@/components/ui/Loader.module.scss";
import { useRouter, useSearchParams } from "next/navigation";
import { createQueryString, transformGuests } from "@/utils/helper";
import { makeQueryString, parseQueryString } from "@/utils/queryString";
import { ROAVE_SEARCH_API_BASE_URL } from "@/config/endpoints";

interface Location {
  name: string;
  code: string;
  type: string;
  label: string;
}
interface LocationSearchProps {
  
}

const PropertySearch: React.FC<LocationSearchProps> = () => {
  const router = useRouter();
  const userCurrency = useAppSelector(getUserCurrency);
  const userCurrentCountry = useAppSelector(getUserCurrentCountry);
  const isLoading = useAppSelector(loading);
  const queryParams: any = useSearchParams();
  // Parse the query string into a payload using parseQueryString
  const decodedQueryParams: any = useMemo(() => {
    if (!queryParams) return null; // Handle case where searchParams isn't ready
    return parseQueryString(queryParams);
  }, [queryParams]);
  const dispatch = useAppDispatch();
  // const [, meta] = useField(name);
  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [preOptions, setPreOptions] = useState<any[]>([]);
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const locationPayload = useAppSelector(getSearchPayload);
  const placesData = useAppSelector(properties);
  const propertyLoading: boolean = useAppSelector(getPropertiesLoading);

  const debounce = (func: Function, delay: number) => {
    let timeout: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), delay);
    };
  };

  const loadOptions = useCallback(
    async (inputValue: string) => {
      console.log("on property selecte fired with loadOptions", inputValue, decodedQueryParams);
      if (!inputValue) return;
      const { payload } = await dispatch(
        getProperties({
          apiUrl: ROAVE_SEARCH_API_BASE_URL,
          url: "api/app/properties",
          payload: {
            q: inputValue,
            city_code: decodedQueryParams?.location?.city_code
              ? decodedQueryParams?.location?.city_code
              : decodedQueryParams?.location?.code,
              type: decodedQueryParams?.location?.type,
            page: 1,
            limit: 500,
          },
        })
      );
      const data = payload?.data?.data;
      if (data?.length > 0) {
        setShowOptions(true);
        setPreOptions(data);
        setDropdownOpen(true);
      }
    },
    [decodedQueryParams]
  );

  const debouncedLoadOptions = useCallback(
    debounce((inputValue: string) => loadOptions(inputValue), 1000),
    []
  );

  /*
  Function to set the preOptions state to the placesData state if it exists
  */
  useEffect(() => {
    if (placesData?.length) {
      setPreOptions(placesData);
    }
  }, [placesData]);

  /*
    Function to handle the change event on the input field to set the search value and trigger the debounced function to load the options
  */
  const handleInputChange = (value: string) => {
    setSearchValue(value);
    if (value?.length >= 2) {
      debouncedLoadOptions(value);
    }
  };

  /*
  Function to handle the click event on the dropdown options to set the selected item and navigate to the search page
  */
  const handleOptionClick = (item: any) => {
    if (item?.name) {
      setSearchValue(item?.name);
      setSelectedItem(item);
      // setFieldValue(name, item); // Set the formik field value
      setShowOptions(false);
      setDropdownOpen(false);
      //Submit request to get single property details
      onSubmit(item);
      //Submit event to parent component to set hotel name in redux & state
      // onChange(item);
    }
  };

  /* 
  Function to handle the click event on the input field to show the dropdown options which are already available in redux
  */
  const handleInputClick = () => {
    console.log("handleInputClick");
    if (preOptions?.length > 0) {
      setShowOptions(true);
    }
  };

  const handleClear = useCallback(
    async (event: MouseEvent<HTMLButtonElement | SVGSVGElement>) => {
      event.stopPropagation();
      setSearchValue("");
      setSelectedItem(null);
      // setFieldValue(name, {}); // Clear the formik field value
      setShowOptions(false);
      setDropdownOpen(false);

      console.log("handleClear before", decodedQueryParams);
      const payload: any = {
        ...decodedQueryParams,
        currency: userCurrency?.code,
        nationality: userCurrency?.nationality_code,
        property_name: undefined,
        property_code: undefined,
        city_code: undefined,
      };
      console.log("handleClear after", payload);
      const queryString = makeQueryString(payload);

      const isSearchPayloadSaved = await dispatch(
        saveSearchPayload(payload)
      ).unwrap();

      if (isSearchPayloadSaved) {
        router.push(`/hotels?${queryString}`);
      }
    },
    [decodedQueryParams, userCurrency, userCurrentCountry, router]
  );

  const LocationSearchSkeleton = () => (
    <>
      {Array?.from({ length: 5 }).map((_, index) => (
        <li
          className="-link d-block col-12 text-left borderradius-40 px-20 py-15 js-search-option mb-1"
          key={index}
        >
          <div className="d-flex">
            <div className="icon-location-2 text-light-1 text-20 pt-4" />
            <div className="ml-10">
              <div className="text-15 lh-12 fw-500 js-search-option-target">
                <Skeleton width={150} />
              </div>
              <div className="text-14 lh-12 text-light-1 mt-5">
                <Skeleton width={100} />
              </div>
            </div>
          </div>
        </li>
      ))}
    </>
  );

  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

  /*
  Function to handle the search request and navigate to the search page
  */
  const onSubmit = useCallback(
    async (data: any) => {
      // console.log("Property search", data, decodedQueryParams);
      const payload: any = {
        ...decodedQueryParams,
        property_name: data?.name,
        property_code: data?.code,
        currency: userCurrency?.code,
        nationality: userCurrency?.nationality_code,
        city_code: data?.city_code,
      };
      console.log("Property search onSubmit", payload);
      const queryString = makeQueryString(payload);

      const isSearchPayloadSaved = await dispatch(
        saveSearchPayload(payload)
      ).unwrap();

      if (isSearchPayloadSaved) {
        const { dates, guests } = payload;

        /*dispatch(
        getHotels({
          url: "v1/hotels/first-available-batch",
          payload: {
            code: data?.code,
            type: "hotel",
            checkin: dates && dates[0],
            checkout: dates && dates[1],
            rooms: JSON.stringify(transformGuests(guests)),
            rates: "concise",
            currency: userCurrency?.code,
            nationality: userCurrentCountry,
            reset: true,
            city_code: data?.city_code
          },
        })
      );*/

        router.push(`/hotels?${queryString}`);
      }
    },
    [decodedQueryParams, userCurrency, userCurrentCountry, router]
  );

  //Set the search value to the property name if it exists
  useEffect(() => {
    if (decodedQueryParams?.property_name) {
      setSearchValue(decodedQueryParams?.property_name);
    }
    else{
      setSearchValue("")
    }
  }, [JSON.stringify(decodedQueryParams)]);
  return (
    <>
      {/* <div className="single-field relative d-flex items-center py-10">
        <input
          className="pl-50 border-light text-dark-1 h-50 rounded-8"
          type="email"
          placeholder="e.g. Best Western"
          required
        />
        <button type="submit" className="absolute d-flex items-center h-full">
          <i className="icon-search text-20 px-15 text-dark-1" />
        </button>
      </div> */}
      <div className="single-field relative d-flex items-center">
      {/* <div className="searchMenu-loc px-30 lg:py-20 lg:px-0 js-form-dd js-liverSearch"> */}
        {/* {JSON.stringify(searchValue)} */}
        <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown} className="col-12">
          <DropdownToggle caret={false} tag="div" className="dropdown-toggle">
            {/* <h4 className="text-15 fw-500 ls-2 lh-16">Location</h4> */}
            <div className="text-15 text-light-1 ls-2 lh-16 d-flex align-items-center">
              <input
                autoComplete="off"
                type="search"
                className="border-light text-dark-1 h-50 borderradius-40"
                // className={`js-search js-dd-focus position-relative`}
                value={searchValue || ""}
                onChange={(e) =>
                  handleInputChange(e?.target?.value.replace(/&/g, "and"))
                }
                onClick={handleInputClick}
                disabled={isLoading}
              />
              {searchValue && (
                <XLg
                  title="Reset"
                  className="clear-icon ml-2"
                  onClick={(e: MouseEvent<SVGSVGElement>) => handleClear(e)}
                  size={15}
                />
              )}
              {propertyLoading && (
                <ArrowClockwise className={styles.spinner} size={15} />
              )}
              {/* <ErrorMessage name={"location.code"}  component="div" className="error-message" /> */}
              {/* {errors[name] && touched[name] && (
              <div className="text-danger">{errors[name]}</div>
            )} */}
            </div>
          </DropdownToggle>

          <DropdownMenu
            className="shadow-2   searchMenu-loc__search-bar-dropdown-property  borderradius-40"
            end
            autoClose="outside"
          >
            <div className="bg-white px-20 py-20 sm:px-0 sm:py-15 borderradius-40 scrollable-container">
              <ul className="y-gap-5 js-results">
                {!loading && preOptions?.length == 0 && (
                  <li
                    className={`-link d-block col-12 text-left borderradius-40 px-20 py-15 js-search-option mb-1 `}
                  >
                    Type or search
                  </li>
                )}
                {propertyLoading ? (
                  <LocationSearchSkeleton />
                ) : (
                  preOptions?.map((item: any) => (
                    <li
                      className={`-link d-block col-12 text-left borderradius-40 px-20 py-15 js-search-option mb-1 ${
                        selectedItem?.code === item?.document?.code
                          ? "active"
                          : ""
                      }`}
                      key={item?.document?.code}
                      role="button"
                      onClick={() => handleOptionClick(item?.document)}
                    >
                      <div className="d-flex">
                        <div>
                          {item?.type === "city" ? (
                            <GeoAlt className="text-light-1" size={20} />
                          ) : (
                            <Buildings className="text-light-1" size={20} />
                          )}
                        </div>
                        <div className="ml-10">
                          <div className="text-15 lh-12 fw-500">
                            {item?.document?.name}
                          </div>
                          <div className="text-14 lh-12 text-light-1 mt-5">
                            {item?.document?.label}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))
                )}
              </ul>
            </div>
          </DropdownMenu>
          
        </Dropdown>
      </div>
    </>
  );
};

export default PropertySearch;
